import { RouteComponentProps } from '@reach/router';
import { graphql } from '../../../../gql';
import { useMutation, useLazyQuery } from '@apollo/client';

// mui
import { styled } from '@mui/material';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';

// mui icons
import CopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useAuth, determineBaseURL } from '@calefy-inc/authentication';
// import { TextField, TextArea } from '@calefy-inc/informedMaterial';
import TextField from '@mui/material/TextField';
import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { GET_ALL_REFERRAL_CODES } from '../../../../queries';
import { Link } from '@reach/router';
import { format } from 'date-fns';
import { GetAllReferralCodesQuery } from '../../../../gql/graphql';
import { copyTextToClipboard } from '@calefy-inc/utility';
import { EditReferralCodeButton } from './EditReferralCodeButton';

type ReferralCode = NonNullable<
  GetAllReferralCodesQuery['getAllReferralCodes']
>[0];

interface ReferralCodePanelProps extends RouteComponentProps {}

export const ReferralCodePanel = (_props: ReferralCodePanelProps) => {
  const { token } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [
    getAllReferralCodes,
    { data: allCodesData, loading: allCodesLoading },
  ] = useLazyQuery(GET_ALL_REFERRAL_CODES);
  const [referralCodes, setReferralCodes] = useState<Array<ReferralCode>>([]);

  useEffect(() => {
    if (token) {
      getAllReferralCodes({ variables: { token } });
    }
  }, [token]);

  useEffect(() => {
    if (allCodesData) {
      setReferralCodes(allCodesData.getAllReferralCodes);
    }
  }, [allCodesData]);

  return (
    <>
      <Typography variant='h4' gutterBottom>
        Referral Code Manager
      </Typography>

      <CreateReferralCodeCard />

      <Card>
        <CardHeader
          title='Existing Referral Codes'
          subheader={`${
            referralCodes.length === 0 ? 'No' : referralCodes.length
          } referral code${referralCodes.length !== 1 ? 's' : ''}`}
        />
        <CardContent>
          {allCodesLoading ? (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress />
            </Box>
          ) : !allCodesLoading && referralCodes.length === 0 ? (
            <NoReferralCodes />
          ) : (
            <TableContainer>
              <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '15%' }}>
                      <strong>Code</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Description</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Associated Quotes</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Referral URL</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {referralCodes.map((code) => {
                    const referralUrl = `${determineBaseURL()}/insurtech/referral/${
                      code?.code
                    }`;
                    const quoteCount = code?.quotes?.length || 0;

                    return (
                      <TableRow key={code?.code} hover>
                        <TableCell>
                          <Stack
                            direction='row'
                            spacing={1}
                            alignItems='center'
                          >
                            <EditReferralCodeButton referralCode={code} />
                            <Typography variant='body2' fontWeight='medium'>
                              {code?.code}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <DescriptionTypography
                            variant='body2'
                            color='text.secondary'
                          >
                            {code?.description || 'No description'}
                          </DescriptionTypography>
                        </TableCell>
                        <TableCell>
                          <Accordion
                            sx={{ backgroundColor: 'transparent' }}
                            disabled={quoteCount <= 0}
                          >
                            {quoteCount > 0 ? (
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Chip
                                  label={`${quoteCount} quote${
                                    quoteCount !== 1 ? 's' : ''
                                  }`}
                                  size='small'
                                />
                              </AccordionSummary>
                            ) : (
                              <AccordionSummary>
                                <Chip
                                  label={`${quoteCount} quote${
                                    quoteCount !== 1 ? 's' : ''
                                  }`}
                                  size='small'
                                />
                              </AccordionSummary>
                            )}
                            <AccordionDetails>
                              <List>
                                {code?.quotes?.map((quote, index) => (
                                  <ListItem key={quote.uniqueID || index}>
                                    {quote.uniqueID ? (
                                      <Link
                                        to={`../quotes/details/${quote.uniqueID}`}
                                      >
                                        {quote?.structuredData?.businessName ??
                                          quote?.structuredData?.contactName ??
                                          'Unknown name'}
                                        {', '}
                                        {format(
                                          new Date(quote?.dateAdded),
                                          'MMM dd, yyyy',
                                        )}
                                      </Link>
                                    ) : (
                                      <>{quote.friendlyId || 'No ID'}</>
                                    )}
                                  </ListItem>
                                ))}
                              </List>
                            </AccordionDetails>
                          </Accordion>
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction='row'
                            spacing={1}
                            justifyContent='left'
                          >
                            <ReferralURLTypography variant='body2'>
                              {referralUrl}
                            </ReferralURLTypography>
                            <Tooltip title='Copy URL to clipboard'>
                              <IconButton
                                size='small'
                                onClick={async () => {
                                  await copyTextToClipboard(
                                    referralUrl,
                                    () =>
                                      enqueueSnackbar('Copied to clipboard!', {
                                        variant: 'success',
                                      }),
                                    () =>
                                      enqueueSnackbar(
                                        'Failed to copy to clipboard',
                                        { variant: 'error' },
                                      ),
                                  );
                                }}
                              >
                                <CopyIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </>
  );
};

const CreateReferralCodeCard = () => {
  const { token } = useAuth();
  const [referralCodeName, setReferralCodeName] = useState<string>('');
  const [referralCodeDescription, setReferralCodeDescription] =
    useState<string>('');
  // explanatory text for any error for the code itself
  const [codeError, setCodeError] = useState<undefined | string>();
  const { enqueueSnackbar } = useSnackbar();
  const [createReferralCode, { loading, error }] = useMutation(
    CREATE_REFERRAL_CODE,
    {
      refetchQueries: () => {
        if (!token) return [];
        return [{ query: GET_ALL_REFERRAL_CODES, variables: { token } }];
      },
    },
  );

  const handleCreateReferralCode = () => {
    if (referralCodeName) {
      createReferralCode({
        variables: {
          code: referralCodeName.trim(),
          description: referralCodeDescription.trim() || null,
          token: token,
        },
      }).then((result) => {
        if (result?.data?.createReferralCode?.referralCode) {
          enqueueSnackbar('Referral code created successfully!', {
            variant: 'success',
          });
          setReferralCodeName('');
          setReferralCodeDescription('');
        }
      });
    }
  };

  return (
    <Card sx={{ marginBottom: 4 }}>
      <CardHeader
        title='Create New Referral Code'
        avatar={<AddIcon color='primary' />}
      />
      <CardContent>
        <Stack spacing={2}>
          <TextField
            label='Referral Code'
            placeholder='Enter a unique referral code'
            value={referralCodeName}
            onChange={(e) => {
              const value = (e.target.value || '').trim();
              setReferralCodeName(value);
              setCodeError(validateReferralCode(value));
            }}
            error={!!codeError}
            helperText={codeError}
            required
          />
          <TextField
            label='Description (Optional)'
            placeholder='Enter an optional description for this referral code'
            value={referralCodeDescription}
            multiline={true}
            rows={5}
            onChange={(e) => setReferralCodeDescription(e.target.value)}
          />
          {error &&
            (error.message.includes('UNIQUE constraint failed') ? (
              <Typography color='error'>
                That referral code already exists. Please create a new referral
                code with a unique name.
              </Typography>
            ) : (
              <Typography color='error'>
                An unexpected error occurred. Please try again later.
              </Typography>
            ))}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant='contained'
              startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
              onClick={handleCreateReferralCode}
              disabled={loading || !referralCodeName || !!codeError}
            >
              {loading ? 'Creating...' : 'Create Code'}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const validateReferralCode = (code: string | undefined) => {
  //console.log(`In validateReferralCode with ${code}`);
  let error: undefined | string;
  if (!code) {
    error = 'This field is required';
  } else if (code.match(/[^a-z0-9_]/i)) {
    error = 'The code can only contains number, letters, and the underscore';
  }
  //console.log(`About to return error=${error}`);
  return error;
};

const DescriptionTypography = styled(Typography)(() => ({
  //   maxWidth: 200,
  wordWrap: 'break-word',
  whiteSpace: 'normal',
}));

const ReferralURLTypography = styled(Typography)(() => ({
  // maxWidth: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const NoReferralCodes = () => {
  return (
    <Typography>
      No existing referral codes found! You can create some above.
    </Typography>
  );
};

const CREATE_REFERRAL_CODE = graphql(`
  mutation CreateReferralCode(
    $code: String!
    $description: String
    $token: String!
  ) {
    createReferralCode(code: $code, description: $description, token: $token) {
      referralCode {
        id
        code
        description
      }
    }
  }
`);
